import { useState } from 'react'

const DRAFT_PREFIX = 'procyon-wizard-draft-'

/** Drops every wizard draft (Close, Cancel, logout); they can hold private keys and tokens. */
const clearWizardDrafts = () => {
  try {
    Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i))
      .filter((k) => k?.startsWith(DRAFT_PREFIX))
      .forEach((k) => sessionStorage.removeItem(k))
  } catch {
    // Storage unavailable: nothing was persisted.
  }
}

/**
 * Draft persistence for the onboarding wizards, backed by sessionStorage.
 *
 * A reload, hot-rebuild or accidental navigation previously threw away a
 * half-done wizard, including a freshly generated private key that may
 * already be pasted into the platform (the setup SQL / cert upload). The
 * draft survives all of those within the same browser tab; the form clears
 * it on completion so the next onboarding starts clean.
 *
 * Deliberately sessionStorage, not localStorage: a wizard half-state is
 * tab-scoped scratch, not something that should resurrect days later in a
 * different tab. Transient check results (verification panels, spinner
 * flags) are NOT persisted, they re-run live, so no stale green cards.
 *
 *   const [draft, setDraft] = useWizardPersist('snowflake', { enabled: !isEditMode })
 *   const [accountId, setAccountId] = useState(() => draft?.accountId ?? '')
 *   ...
 *   useEffect(() => { if (!completedRef.current) setDraft({ step, accountId, ... }) })
 *
 * Restoring happens once (lazy initializer). Saving is the caller's effect
 * so the snapshot can reference state declared after the hook. Bump
 * `version` when a wizard's draft shape changes to drop stale drafts.
 */
const useWizardPersist = (wizardKey, { enabled = true, version = 1 } = {}) => {
  const storageKey = `${DRAFT_PREFIX}${wizardKey}-v${version}`

  const [restored] = useState(() => {
    if (!enabled) return null
    try {
      const raw = sessionStorage.getItem(storageKey)
      return raw ? JSON.parse(raw) : null
    } catch {
      // Private-browsing quota errors etc: persistence is best-effort.
      return null
    }
  })

  const setDraft = (next) => {
    if (!enabled) return
    try {
      if (next === null) sessionStorage.removeItem(storageKey)
      else sessionStorage.setItem(storageKey, JSON.stringify(next))
    } catch {
      // Persistence is an optimization, never a failure.
    }
  }

  return [restored, setDraft]
}

export { useWizardPersist, clearWizardDrafts }
